import React from 'react';
import { connect } from 'react-redux';
import QueryString from 'query-string';
import ReactTable from "react-table";
import ScrollArea from 'react-scrollbar';
import i18next from 'i18next';

import { Message } from '../Message';
import { PageTitle } from '../PageTitle';
import { RowButton } from '../RowButton';
import { ScrollModule } from '../ScrollModule';
import { UsersService } from '../../services';
import { AccessHelper, BaseTable } from '../../helpers';

@connect(state => {
  return {
    me: state.user.me
  }
})
class Users extends React.Component {
  constructor(props) {
    super(props);

    const passedState = this.props?.history?.location?.state ? this.props.history.location.state : null;

    this.DEFAULT_SORTED = [{ id: 'lastName', desc: false }, { id: 'firstName', desc: false }];

    this.state = {
      localErrorHandler: this.handleLocalError.bind(this),
      localErrorMessage: null,
      hasError: false,
      data: [],
      pageCount: null,
      forcePage: null,
      totalElements: null,
      loading: true,
      parsedOptions: QueryString.parse(this.props.location.search),
      currentHistoryCount: passedState && passedState.currentHistoryCount ? passedState.currentHistoryCount : 0,
      viewAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/users/view/:userId'),
      editAllowed: AccessHelper.userHasAccess(this.props.me.availableRoutes, '/admin/users/edit/:userId'),
      searchFirst: passedState && passedState.searchTerms && passedState.searchTerms.first ? passedState.searchTerms.first : '',
      searchLast: passedState && passedState.searchTerms && passedState.searchTerms.last ? passedState.searchTerms.last : '',
      searchUsername: passedState && passedState.searchTerms && passedState.searchTerms.username ? passedState.searchTerms.username : ''
    };
  }

  messageDismissCallback() {
    this.setState({ localErrorMessage: null });
  }

  renderMessages() {
    if (this.state.localErrorMessage) {
      return (
          <Message errorMessage={this.state.localErrorMessage} messageDismissCallback={this.messageDismissCallback.bind(this)} />
      );
    }

    if (this.props.location.state) {
      return (
          <Message messages={this.props.location} />
      );
    }
    return null;
  }

  handleLocalError(localErrorMessage) {
    this.setState({ localErrorMessage: localErrorMessage });
  }

  doSearch(event) {
    const { target: { name, value }} = event;
    this.setState({[name]: value});
    if (this.state.timeout) {
      clearTimeout(this.state.timeout);
    }
    this.state.timeout = setTimeout(() => {
      this.updateSearchValue(name, value)
    }, 500);
   }
 
   updateSearchValue(name, val) {
     this.setState({[name]: val}, () => {
       this.fetchData(this.refs.usersTable && this.refs.usersTable.state ? this.refs.usersTable.state : null);
     });
   }

  fetchData(tableData) {
    if (this.state.hasError) {
      return;
    }

    let currentFirst = this.state.searchFirst;
    let currentLast = this.state.searchLast;
    let currentUsername = this.state.searchUsername;

    if (currentFirst !== this.state.searchFirst || currentLast !== this.state.searchLast || currentUsername !== this.state.searchUsername) {
      return; // not current request
    }

    if (this.updateRequestURL(tableData)) {
      return;
    }

    this.setState({
      loading: true
    });

    let options = BaseTable.buildFetchDataOptions(this.state.parsedOptions, this.DEFAULT_SORTED);

    let isSearch = false;
    if (currentFirst) {
      options.push({ 'searchFirst': currentFirst });
      isSearch = true;
    }

    if (currentLast) {
      options.push({ 'searchLast': currentLast });
      isSearch = true;
    }

    if (currentUsername) {
      options.push({ 'searchUsername': currentUsername });
      isSearch = true;
    }

    UsersService.getAll(options, isSearch).then(response => {
      const pageCount = Math.ceil(response.page.totalElements / this.refs.usersTable.state.pageSize);

      let forcePage = (pageCount > 0 && tableData.page >= pageCount) ? pageCount - 1 : null;

      this.setState({
        data: response.users,
        pageCount: pageCount,
        forcePage: forcePage,
        totalElements: response.page.totalElements,
        loading: false
      });
    }).catch(error => {
      console.error(error);
      this.setState({ hasError: true });
      this.state.localErrorHandler(error.message);
    });
  }

  updateRequestURL(tableData) {
    let options = BaseTable.buildURLOptions(tableData, this.DEFAULT_SORTED);

    let params = options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&');

    const currentSearch = this.props.location.search ? this.props.location.search : '?';

    if (`?${params}` !== currentSearch) {
      if (this.state.currentHistoryCount > 100) {
        console.error('Exceeded the history count stack of 100.');
        return false;
      }
      this.props.history.push({
        search: `?${params}`,
        state: {
          searchTerms: {first: this.state.searchFirst, last: this.state.searchLast, username: this.state.searchUsername},
          currentHistoryCount: this.state.currentHistoryCount + 1
        }
      });
      return true;
    } else {
      return false;
    }
  }

  render() {
    let dynamicOptions = BaseTable.buildDynamicOptions(this.state.parsedOptions, this.state.forcePage, this.DEFAULT_SORTED);

    return (
        <div className="animated fadeIn">
          <div className="row">
            <PageTitle title={i18next.t('pages.users.header')}
                       addLinkWithParams={{
                         link: {
                           pathname: "/admin/users/add",
                           state: {
                             backLinkSearch: this.props.location.search,
                             searchTerms: {first: this.state.searchFirst, last: this.state.searchLast, username: this.state.searchUsername}
                           }
                         }
                       }} />
          </div>

          {this.renderMessages()}

          <div className="op-content-box">
            <ScrollModule/>
            <ScrollArea
                speed={0.8}
                className="scroll-container"
                contentClassName="scrollable-content"
                horizontal={true} >
              <div className="row">
                <div className="col-sm-12">
                  <div className="card">
                    <div className="card-block">
                      <div className="react-table-custom">
                        <ReactTable
                          className="-striped -highlight"
                          ref="usersTable"
                          manual // Forces table not to paginate or sort automatically, so we can handle it server-side
                          data={this.state.data}
                          pages={this.state.pageCount} // Display the total number of pages
                          loading={this.state.loading} // Display the loading overlay when we need it
                          totalElements={this.state.totalElements}
                          onFetchData={this.fetchData.bind(this)} // Request new data when things change
                          showPaginationTop
                          columns={[
                                      {
                                        Header: i18next.t('table-headers.first-name'),
                                        id: 'firstName',
                                        accessor: "firstName",
                                        sortable: true,
                                        minResizeWidth: 50,
                                        filterable: true,
                                        Filter: <div className="filter">
                                          <input
                                            style={{width: "100%", margin:"0 auto"}}
                                            value = {this.state.searchFirst}
                                            onChange={this.doSearch.bind(this)}
                                            type="text"
                                            name="searchFirst"
                                            placeholder={ i18next.t('form-labels.search-by-first-name') }
                                            className="table-search"
                                            autoComplete="off"
                                          />
                                      </div>
                                      },
                                      {
                                        Header: i18next.t('table-headers.last-name'),
                                        accessor: "lastName",
                                        sortable: true,
                                        minResizeWidth: 50,
                                        filterable: true,
                                        Filter: <div className="filter">
                                          <input
                                            style={{width: "100%", margin:"0 auto"}}
                                            value = {this.state.searchLast}
                                            onChange={this.doSearch.bind(this)}
                                            type="text"
                                            name="searchLast"
                                            placeholder={ i18next.t('form-labels.search-by-last-name') }
                                            className="table-search"
                                            autoComplete="off"
                                          />
                                      </div>
                                      },
                                      {
                                        Header: i18next.t('table-headers.username'),
                                        accessor: "username",
                                        sortable: true,
                                        minResizeWidth: 50,
                                        filterable: true,
                                        Filter: <div className="filter">
                                          <input
                                            style={{width: "100%", margin:"0 auto"}}
                                            value = {this.state.searchUsername}
                                            onChange={this.doSearch.bind(this)}
                                            type="text"
                                            name="searchUsername"
                                            placeholder={ i18next.t('form-labels.search-by-username') }
                                            className="table-search"
                                            autoComplete="off"
                                          />
                                      </div>
                                      },
                                      {
                                        Cell: cell => <RowButton
                                                      allowView={this.state.viewAllowed}
                                                      allowEdit={!cell.original.managedExternally && this.state.editAllowed}
                                                      viewPath={{
                                                        pathname: `/admin/users/view/${cell.original.id}`,
                                                        state: {
                                                          backLinkSearch: this.props.location.search,
                                                          searchTerms: {first: this.state.searchFirst, last: this.state.searchLast, username: this.state.searchUsername}
                                                        }
                                                      }}
                                                      editPath={{
                                                        pathname: `/admin/users/edit/${cell.original.id}`,
                                                        state: {
                                                          backLinkSearch: this.props.location.search,
                                                          searchTerms: {first: this.state.searchFirst, last: this.state.searchLast, username: this.state.searchUsername}
                                                        }
                                                      }} />,
                                        sortable: false,
                                        minResizeWidth: 50,
                                        width: 150
                                      }
                                    ]}
                          {...dynamicOptions}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
    );
  }
}

export default Users;
